import { FC, useContext, useEffect, useMemo, useState } from "react";
import "./BasicTab.css";
import Background from "../../../assets/default__background.jpg";
import DefaultCompanyLogo from "../../../assets/logo.svg";
import { Context } from "../../../index";
import CrossBlue from "../../../assets/cancel.svg";
import { AppContext } from "../../../App";
import ImageSelector from "../../Shared/ImageSelector/ImageSelector";
import type { IEditElemSettings, IElemSettings } from "../../../common/api_interfaces/settings/IElemSettings";

const BasicTab: FC = () => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    const [systemSettings, setSystemSettings] = useState<IElemSettings[]>([]);

    const [newName, setNewName] = useState<string>();
    const [newDescription, setNewDescription] = useState<string>();
    const [newLogo, setNewLogo] = useState<File>();
    const [newCover, setNewCover] = useState<File>();

    const companyName = useMemo(
        () => systemSettings.find((param) => param.paramName === "name")?.value,
        [systemSettings]
    );
    const companyDescription = useMemo(
        () => systemSettings.find((param) => param.paramName === "description")?.value,
        [systemSettings]
    );
    const [logoBase64, setLogoBase64] = useState<string>(DefaultCompanyLogo);
    const [coverBase64, setCoverBase64] = useState<string>(Background);

    const [version, setVersion] = useState<any>();

    useEffect(() => {
        (async () => {
            try {
                const versionResp = await store.getVersion();
                setVersion(versionResp);
            } catch (err) {
                console.error(err);
            }
            try {
                const data = await store.getSystemSettings();
                const logo = (await store.commonService.getSystemLogo()).data;
                const cover = (await store.commonService.getSystemCover()).data;

                if (logo) setLogoBase64(logo);
                if (cover) setCoverBase64(cover);
                setSystemSettings(data);
            } catch (error) {
                console.log(error);
            }
        })();
    }, []);

    function checkUpdate(): boolean {
        return newLogo !== undefined || newCover !== undefined || newName !== undefined || newDescription !== undefined;
    }

    async function saveChange() {
        let logoFileId = undefined;
        let coverFileId = undefined;

        if (newLogo) {
            const resp = await store.fileService.uploadTempFile(newLogo);
            logoFileId = resp.data.id;
        }

        if (newCover) {
            const resp = await store.fileService.uploadTempFile(newCover);
            coverFileId = resp.data.id;
        }

        const allParams: IEditElemSettings[] = [];

        if (newName) {
            allParams.push({ paramName: "name", value: newName, fileId: undefined });
        }
        if (newDescription) {
            allParams.push({ paramName: "description", value: newDescription, fileId: undefined });
        }
        if (newLogo) {
            allParams.push({ paramName: "logo", value: undefined, fileId: logoFileId });
        }
        if (newCover) {
            allParams.push({ paramName: "cover", value: undefined, fileId: coverFileId });
        }

        const newSettings = allParams.filter(
            (newParam) => !systemSettings.some((oldParam) => oldParam.paramName === newParam.paramName)
        );
        const changedSettings = allParams.filter((newParam) =>
            systemSettings.some((oldParam) => oldParam.paramName === newParam.paramName)
        );

        if (newSettings.length) {
            await store.editSystemSettings(newSettings);
        }
        if (changedSettings.length) {
            await store.editSystemSettings(changedSettings);
        }
        if (newSettings.length || changedSettings.length) {
            const data = await store.getSystemSettings();
            setSystemSettings(data);
            showToast("Настройки сохранены");
        }
    }

    function deleteCover() {
        (async () => {
            try {
                await store.deleteImg("cover");
                const data = await store.getSystemSettings();
                setSystemSettings(data);
                setNewCover(undefined);
            } catch (error) {
                console.log(error);
            }
        })();
    }

    function deleteLogo() {
        (async () => {
            try {
                await store.deleteImg("logo");
                const data = await store.getSystemSettings();
                setSystemSettings(data);
                setNewLogo(undefined);
            } catch (error) {
                console.log(error);
            }
        })();
    }

    function defaultsettings() {
        setNewName(undefined);
        setNewDescription(undefined);
        setNewLogo(undefined);
        setNewCover(undefined);
    }

    return (
        <div className="basic__tab">
            <div>
                <h2 className="basic__tab--setting__elem--h2">Обложка</h2>
                <div className="basic__tab--setting__elem--cover__block">
                    <div className="basic__tab--setting__elem--cover">
                        <img src={newCover ? URL.createObjectURL(newCover) : coverBase64} />
                    </div>
                    <div className="basic__tab--setting__elem--company__logo__button">
                        <ImageSelector
                            onImageCropped={(file) => {
                                setNewCover(file);
                            }}
                        />
                        <button
                            className="basic__tab--setting__elem--company__logo--button__cross"
                            onClick={deleteCover}
                        >
                            <img src={CrossBlue} />
                        </button>
                    </div>
                </div>
            </div>
            <div className="basic__tab--setting__elem">
                <h2 className="basic__tab--setting__elem--h2">Логотип</h2>
                <div className="basic__tab--setting__elem--cover__block--logo">
                    <div className="basic__tab--setting__elem--company__logo">
                        <img src={newLogo ? URL.createObjectURL(newLogo) : logoBase64} />
                    </div>
                    <div className="basic__tab--setting__elem--company__logo__button">
                        <ImageSelector
                            onImageCropped={(file) => {
                                setNewLogo(file);
                            }}
                        />
                        <button
                            className="basic__tab--setting__elem--company__logo--button__cross"
                            onClick={deleteLogo}
                        >
                            <img src={CrossBlue} />
                        </button>
                    </div>
                </div>
            </div>
            <div className="basic__tab--setting__elem">
                <h2 className="basic__tab--setting__elem--h2">Название</h2>
                <div className="basic__tab--setting__elem--name__company">
                    <input
                        className="basic__tab--setting__elem--name__input"
                        value={newName ?? companyName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Название компании"
                        type="text"
                    />
                </div>
            </div>
            <div className="basic__tab--setting__elem">
                <h2 className="basic__tab--setting__elem--h2">Слоган</h2>
                <div className="basic__tab--setting__elem--name__company">
                    <textarea
                        className="basic__tab--setting__elem--name__input"
                        value={newDescription ?? companyDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                        placeholder="Текст слогана"
                        cols={30}
                        rows={2}
                    />
                </div>
            </div>
            <div className="basic__tab--setting__elem">
                <h2 className="basic__tab--setting__elem--h2">Тема</h2>
                <div className="basic__tab--setting__elem--topic">
                    <h2>Стандартная тема</h2>
                </div>
            </div>
            <div className="basic__tab--setting__elem">
                <h2 className="basic__tab--setting__elem--h2">Версия</h2>
                <div className={`basic__tab--setting__elem--topic `}>
                    <h2 className={`${version?.appVersion !== version?.dbVersion ? "error" : ""}`}>
                        Приложение: {version?.appVersion} База данных: {version?.dbVersion}
                    </h2>
                </div>
            </div>
            {checkUpdate() === true && (
                <div className="basic__tab--setting__elem--buttons">
                    <button
                        data-testId="button-basic-settings-save"
                        className="basic__tab--modal__window--block__button--crop"
                        onClick={saveChange}
                    >
                        {"Сохранить"}
                    </button>
                    <button
                        data-testId="button-basic-settings-cancel"
                        className="basic__tab--modal__window--block__button--close"
                        onClick={defaultsettings}
                    >
                        {"Отмена"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default BasicTab;
